import { ErroHttp, lerRetryAfterSegundos } from '@/shared/utils/erroHttp';
import { getApiErrorMessage } from '@/shared/utils/apiResponse';
import { apiGet, apiPost } from '@/shared/utils/requestBuilder';

import type {
  CancelarFaturaInput,
  ComprovanteInput,
  DescontoInput,
  EstornoInput,
  EstornoSolicitado,
  FaturaDoProcesso,
  LinhaAr,
  PaginaFinanceiro,
  ResumoFinanceiroProcesso,
  SegundaViaInput,
  SolicitacaoEstornoInput,
  ZerarConsumoInput,
} from '../types';

const TIMEOUT_LEITURA_MS = 15_000;
const TIMEOUT_ESCRITA_MS = 30_000;

interface RespostaPaginada<T> {
  data: T[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

function base(processoId: string): string {
  return `/admin/arbitral/processos/${processoId}/financeiro`;
}

function lerDoServidor(
  endpoint: string,
  params?: Record<string, string | number | boolean>,
): Promise<Response> {
  return apiGet(endpoint, params, false, AbortSignal.timeout(TIMEOUT_LEITURA_MS));
}

function escreverNoServidor(endpoint: string, body: object | FormData): Promise<Response> {
  return apiPost(endpoint, body, false, AbortSignal.timeout(TIMEOUT_ESCRITA_MS));
}

async function erroDaResposta(response: Response, fallback: string): Promise<never> {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  throw new ErroHttp(
    getApiErrorMessage(payload, `${fallback} (status ${response.status})`),
    response.status,
    lerRetryAfterSegundos(response.headers),
  );
}

async function lerDado<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) await erroDaResposta(response, fallback);
  let corpo: unknown;
  try {
    corpo = await response.json();
  } catch {
    throw new ErroHttp(`${fallback} (resposta sem corpo)`, response.status);
  }
  const envelope = corpo as { data?: unknown } | null;
  return (envelope?.data ?? corpo) as T;
}

async function lerPagina<T>(response: Response, fallback: string): Promise<PaginaFinanceiro<T>> {
  const dado = await lerDado<RespostaPaginada<T>>(response, fallback);
  return {
    itens: dado.data ?? [],
    page: dado.pagination?.page ?? 1,
    limit: dado.pagination?.limit ?? 0,
    total: dado.pagination?.total ?? 0,
    totalPages: dado.pagination?.totalPages ?? 1,
  };
}

export const financeiroProcessoService = {
  obterResumo: async (processoId: string): Promise<ResumoFinanceiroProcesso> =>
    lerDado(await lerDoServidor(base(processoId)), 'Erro ao carregar o financeiro do processo'),

  listarFaturas: async (
    processoId: string,
    page: number,
    limit: number,
  ): Promise<PaginaFinanceiro<FaturaDoProcesso>> =>
    lerPagina(
      await lerDoServidor(`${base(processoId)}/faturas`, { page, limit }),
      'Erro ao carregar as faturas do processo',
    ),

  listarArs: async (
    processoId: string,
    page: number,
    limit: number,
  ): Promise<PaginaFinanceiro<LinhaAr>> =>
    lerPagina(
      await lerDoServidor(`${base(processoId)}/extrato`, { page, limit }),
      'Erro ao carregar o consumo do processo',
    ),

  cancelarFatura: async (input: CancelarFaturaInput): Promise<FaturaDoProcesso> =>
    lerDado(
      await escreverNoServidor(
        `${base(input.processoId)}/faturas/${input.invoiceId}/cancelamento`,
        {
          motivo: input.motivo,
          cienteEnvioCancelado: input.cienteEnvioCancelado,
        },
      ),
      'Erro ao cancelar a fatura',
    ),

  enviarSegundaVia: async (input: SegundaViaInput): Promise<FaturaDoProcesso> =>
    lerDado(
      await escreverNoServidor(`${base(input.processoId)}/faturas/${input.invoiceId}/segunda-via`, {
        canais: input.canais,
        mensagem: input.mensagem,
      }),
      'Erro ao enviar a segunda via',
    ),

  anexarComprovante: async (input: ComprovanteInput): Promise<FaturaDoProcesso> => {
    const form = new FormData();
    form.append('file', input.arquivo);
    form.append('pagoEm', input.pagoEm);
    form.append('valorConfirmado', input.valorConfirmado);
    if (input.observacao) form.append('observacao', input.observacao);
    return lerDado(
      await escreverNoServidor(
        `${base(input.processoId)}/faturas/${input.invoiceId}/comprovante`,
        form,
      ),
      'Erro ao anexar o comprovante',
    );
  },

  aplicarDesconto: async (input: DescontoInput): Promise<FaturaDoProcesso> =>
    lerDado(
      await escreverNoServidor(`${base(input.processoId)}/faturas/${input.invoiceId}/desconto`, {
        motivo: input.motivo,
        itens: input.itens,
        descontoFechamento: input.descontoFechamento,
      }),
      'Erro ao aplicar o desconto',
    ),

  estornarPagamento: async (input: EstornoInput): Promise<EstornoSolicitado> =>
    lerDado(
      await escreverNoServidor(`${base(input.processoId)}/faturas/${input.invoiceId}/estorno`, {
        motivo: input.motivo,
        cienteDevolucaoAoPagador: input.cienteDevolucaoAoPagador,
      }),
      'Erro ao estornar o pagamento',
    ),

  solicitarEstorno: async (input: SolicitacaoEstornoInput): Promise<FaturaDoProcesso> =>
    lerDado(
      await escreverNoServidor(
        `${base(input.processoId)}/faturas/${input.invoiceId}/solicitacao-estorno`,
        { motivo: input.motivo },
      ),
      'Erro ao solicitar o estorno',
    ),

  zerarConsumo: async (input: ZerarConsumoInput): Promise<{ eventoId: string }> =>
    lerDado(
      await escreverNoServidor(`${base(input.processoId)}/extrato/${input.eventoId}/zerar`, {
        motivo: input.motivo,
      }),
      'Erro ao zerar o item',
    ),

  reativarConsumo: async (input: ZerarConsumoInput): Promise<{ eventoId: string }> =>
    lerDado(
      await escreverNoServidor(`${base(input.processoId)}/extrato/${input.eventoId}/reativar`, {
        motivo: input.motivo,
      }),
      'Erro ao reativar o item',
    ),
};
